import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb } from 'antd';
import { useDocumentTitle } from '../utils/document-head';

export interface Crumb {
  title: ReactNode;
  to?: string;
}

const text = (node: ReactNode) => (typeof node === 'string' || typeof node === 'number' ? String(node).trim() || null : null);

/**
 * Page title with optional breadcrumb trail above it and actions on the right. Also names the browser tab:
 * "Title · First crumb" (e.g. "Ada Lovelace · Contacts"); pass `docTitle` when the visible title is not plain text.
 */
export function PageHeader({ title, subtitle, extra, breadcrumb, docTitle }: { title: ReactNode; subtitle?: ReactNode; extra?: ReactNode; breadcrumb?: Crumb[]; docTitle?: string }) {
  const own = docTitle ?? text(title);
  const section = breadcrumb && breadcrumb.length > 1 ? text(breadcrumb[0].title) : null;
  useDocumentTitle(own && section && section !== own ? `${own} · ${section}` : own);
  return (
    <div className="page-header">
      <div style={{ minWidth: 0 }}>
        {breadcrumb?.length ? <Breadcrumb items={breadcrumb.map((c) => ({ title: c.to ? <Link to={c.to}>{c.title}</Link> : c.title }))} /> : null}
        <h1>{title}</h1>
        {subtitle && <span className="page-subtitle">{subtitle}</span>}
      </div>
      {/* may shrink to the row's width so a wrapping action bar breaks onto a second line instead of overflowing */}
      {extra && <div style={{ flex: '0 1 auto', minWidth: 0, maxWidth: '100%' }}>{extra}</div>}
    </div>
  );
}
